/**
 * Eterna Vaults - Key Management Service
 * 
 * This service manages device-generated encryption keys and secure storage
 * to ensure zero-knowledge architecture and maximum security.
 */

import encryptionService from './EncryptionService';
// CRITICAL FIX: Use IndexedDB instead of sessionStorage/localStorage for encrypted keys
import secureStorage from './SecureStorageService';

class KeyManagementService {
  constructor() {
    this.storageKey = 'eterna_vaults_keys';
    this.sessionKey = 'eterna_vaults_session';
    this.keyExpiry = 24 * 60 * 60 * 1000; // 24 hours
  }

  /**
   * Generate and store device keys for a new user
   * @param {string} userId - User identifier
   * @param {string} passcode - User's passcode
   * @returns {Promise<Object>} Key generation result
   */
  async generateDeviceKeys(userId, passcode) {
    try {
      // Generate master salt for this user
      const masterSalt = encryptionService.generateSalt();
      
      // Generate device-specific salt
      const deviceSalt = encryptionService.generateSalt();
      
      // Generate device fingerprint
      const deviceFingerprint = await this.generateDeviceFingerprint();
      
      // Create key metadata
      const keyMetadata = {
        userId,
        masterSalt,
        deviceSalt,
        deviceFingerprint,
        createdAt: Date.now(),
        keyVersion: '1.0',
        algorithm: 'AES-256-GCM',
        keyDerivation: 'Scrypt'
      };

      // Store keys securely
      await this.storeKeysSecurely(keyMetadata, passcode);
      
      return {
        success: true,
        masterSalt,
        deviceFingerprint,
        keyVersion: keyMetadata.keyVersion
      };
    } catch (error) {
      console.error('Device key generation failed:', error);
      throw new Error('Failed to generate device keys');
    }
  }

  /**
   * Retrieve and validate device keys
   * @param {string} userId - User identifier
   * @param {string} passcode - User's passcode
   * @returns {Promise<Object>} Key retrieval result
   */
  async retrieveDeviceKeys(userId, passcode) {
    try {
      console.log('Retrieving device keys for user:', userId);
      const storedKeys = await this.getStoredKeys();
      console.log('Stored keys found:', !!storedKeys);
      
      if (!storedKeys) {
        console.log('No stored keys found in sessionStorage');
        throw new Error('No keys found for user');
      }
      
      if (storedKeys.userId !== userId) {
        console.log('User ID mismatch:', storedKeys.userId, 'vs', userId);
        throw new Error('No keys found for user');
      }

      // Validate device fingerprint
      const currentFingerprint = await this.generateDeviceFingerprint();
      if (storedKeys.deviceFingerprint !== currentFingerprint) {
        console.log('Device fingerprint mismatch');
        throw new Error('Device fingerprint mismatch - possible security breach');
      }

      // Check key expiry
      if (this.isKeyExpired(storedKeys)) {
        console.log('Keys have expired');
        throw new Error('Keys have expired - re-authentication required');
      }

      console.log('Device keys retrieved successfully');
      return {
        success: true,
        masterSalt: storedKeys.masterSalt,
        deviceSalt: storedKeys.deviceSalt,
        keyVersion: storedKeys.keyVersion
      };
    } catch (error) {
      console.error('Device key retrieval failed:', error);
      throw new Error('Failed to retrieve device keys');
    }
  }

  /**
   * Generate device fingerprint for security
   * @returns {Promise<string>} Device fingerprint
   */
  async generateDeviceFingerprint() {
    try {
      const components = [];
      
      // Browser information
      components.push(navigator.userAgent);
      components.push(navigator.language);
      components.push(screen.width + 'x' + screen.height);
      components.push(new Date().getTimezoneOffset().toString());
      
      // Hardware information (if available)
      if (navigator.hardwareConcurrency) {
        components.push(navigator.hardwareConcurrency.toString());
      }
      
      if (navigator.deviceMemory) {
        components.push(navigator.deviceMemory.toString());
      }
      
      // Create fingerprint hash
      const fingerprint = components.join('|');
      const hash = await this.hashString(fingerprint);
      
      return hash;
    } catch (error) {
      console.error('Device fingerprint generation failed:', error);
      // Fallback to basic fingerprint
      return btoa(navigator.userAgent + navigator.language);
    }
  }

  /**
   * Hash string using Web Crypto API
   * @param {string} input - String to hash
   * @returns {Promise<string>} Hashed string
   */
  async hashString(input) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      // Fallback to simple hash
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash).toString(16);
    }
  }

  /**
   * Store keys securely in browser storage
   * @param {Object} keyMetadata - Key metadata to store
   * @param {string} passcode - User's passcode for encryption
   * @returns {Promise<void>}
   */
  async storeKeysSecurely(keyMetadata, passcode) {
    try {
      console.log('Storing keys securely for user:', keyMetadata.userId);
      
      // Encrypt key metadata before storage
      const salt = encryptionService.generateSalt();
      console.log('Generated salt for key storage');
      
      const encryptedMetadata = await encryptionService.encryptUserData(
        JSON.stringify(keyMetadata),
        passcode,
        salt
      );
      console.log('Encrypted key metadata successfully');

      // CRITICAL FIX: Store encrypted keys in IndexedDB instead of sessionStorage
      await secureStorage.setItem(this.sessionKey, JSON.stringify(encryptedMetadata));
      
      // Migrate from sessionStorage if exists (backward compatibility)
      await secureStorage.migrateFromSessionStorage(this.sessionKey, this.sessionKey);
      
      // Store non-sensitive metadata in localStorage (public data is OK)
      const publicMetadata = {
        userId: keyMetadata.userId,
        keyVersion: keyMetadata.keyVersion,
        createdAt: keyMetadata.createdAt,
        deviceFingerprint: keyMetadata.deviceFingerprint
      };
      
      localStorage.setItem(this.storageKey, JSON.stringify(publicMetadata));
    } catch (error) {
      console.error('Secure key storage failed:', error);
      console.error('Error details:', error.message);
      console.error('Stack trace:', error.stack);
      throw new Error('Failed to store keys securely: ' + error.message);
    }
  }

  /**
   * Retrieve stored keys from browser storage (IndexedDB)
   * CRITICAL: Uses IndexedDB instead of sessionStorage
   * @returns {Promise<Object>} Stored key metadata
   */
  async getStoredKeys() {
    try {
      // Try IndexedDB first
      let encryptedData = await secureStorage.getItem(this.sessionKey);
      
      // Backward compatibility: migrate from sessionStorage if not in IndexedDB
      if (!encryptedData) {
        const migrated = await secureStorage.migrateFromSessionStorage(this.sessionKey, this.sessionKey);
        if (migrated) {
          encryptedData = await secureStorage.getItem(this.sessionKey);
        }
      }
      
      if (!encryptedData) {
        return null;
      }

      return JSON.parse(encryptedData);
    } catch (error) {
      return null;
    }
  }

  /**
   * Decrypt stored keys using passcode
   * @param {string} passcode - User's passcode
   * @returns {Promise<Object>} Decrypted key metadata
   */
  async decryptStoredKeys(passcode) {
    try {
      const encryptedData = await this.getStoredKeys();
      if (!encryptedData) {
        throw new Error('No encrypted keys found');
      }

      const decryptedData = await encryptionService.decryptUserData(encryptedData, passcode);
      return JSON.parse(decryptedData);
    } catch (error) {
      console.error('Key decryption failed:', error);
      throw new Error('Failed to decrypt stored keys');
    }
  }

  /**
   * Check if keys have expired
   * @param {Object} keyMetadata - Key metadata
   * @returns {boolean} True if expired
   */
  isKeyExpired(keyMetadata) {
    const now = Date.now();
    const keyAge = now - keyMetadata.createdAt;
    return keyAge > this.keyExpiry;
  }

  /**
   * Clear all stored keys (logout)
   * CRITICAL: Uses IndexedDB instead of sessionStorage
   * @returns {Promise<void>}
   */
  async clearStoredKeys() {
    try {
      await secureStorage.removeItem(this.sessionKey);
      // Also clear from sessionStorage if exists (cleanup)
      sessionStorage.removeItem(this.sessionKey);
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Rotate device keys for security
   * @param {string} userId - User identifier
   * @param {string} passcode - User's passcode
   * @returns {Promise<Object>} Key rotation result
   */
  async rotateDeviceKeys(userId, passcode) {
    try {
      // Clear old keys
      await this.clearStoredKeys();
      
      // Generate new keys
      const result = await this.generateDeviceKeys(userId, passcode);
      
      return {
        success: true,
        message: 'Device keys rotated successfully',
        newKeyVersion: result.keyVersion
      };
    } catch (error) {
      console.error('Key rotation failed:', error);
      throw new Error('Failed to rotate device keys');
    }
  }

  /**
   * Validate key integrity
   * @param {Object} keyMetadata - Key metadata to validate
   * @returns {boolean} True if valid
   */
  validateKeyIntegrity(keyMetadata) {
    const requiredFields = ['userId', 'masterSalt', 'deviceSalt', 'deviceFingerprint', 'createdAt'];
    return requiredFields.every(field => keyMetadata[field]);
  }

  /**
   * Get key status information
   * CRITICAL: Now async due to IndexedDB migration
   * @returns {Promise<Object>} Key status
   */
  async getKeyStatus() {
    try {
      const publicMetadata = localStorage.getItem(this.storageKey);
      const encryptedKeys = await secureStorage.getItem(this.sessionKey);
      const hasEncryptedKeys = !!encryptedKeys;
      
      if (!publicMetadata) {
        return {
          hasKeys: false,
          status: 'no_keys'
        };
      }

      const metadata = JSON.parse(publicMetadata);
      const isExpired = this.isKeyExpired(metadata);
      
      return {
        hasKeys: true,
        hasEncryptedKeys,
        userId: metadata.userId,
        keyVersion: metadata.keyVersion,
        createdAt: metadata.createdAt,
        isExpired,
        status: isExpired ? 'expired' : 'active'
      };
    } catch (error) {
      // CRITICAL FIX: No console logs - fail silently
      return {
        hasKeys: false,
        status: 'error'
      };
    }
  }

  /**
   * Export keys for backup (encrypted)
   * @param {string} passcode - User's passcode
   * @param {string} backupPasscode - Backup passcode
   * @returns {Promise<string>} Encrypted backup data
   */
  async exportKeysForBackup(passcode, backupPasscode) {
    try {
      const keyMetadata = await this.decryptStoredKeys(passcode);
      const backupData = {
        ...keyMetadata,
        exportedAt: Date.now(),
        backupVersion: '1.0'
      };

      const backupSalt = encryptionService.generateSalt();
      const encryptedBackup = await encryptionService.encryptUserData(
        JSON.stringify(backupData),
        backupPasscode,
        backupSalt
      );

      return JSON.stringify(encryptedBackup);
    } catch (error) {
      console.error('Key backup export failed:', error);
      throw new Error('Failed to export keys for backup');
    }
  }

  /**
   * Import keys from backup
   * @param {string} backupData - Encrypted backup data
   * @param {string} backupPasscode - Backup passcode
   * @param {string} newPasscode - New passcode for storage
   * @returns {Promise<Object>} Import result
   */
  async importKeysFromBackup(backupData, backupPasscode, newPasscode) {
    try {
      const encryptedBackup = JSON.parse(backupData);
      const decryptedBackup = await encryptionService.decryptUserData(encryptedBackup, backupPasscode);
      const keyMetadata = JSON.parse(decryptedBackup);

      // Validate backup integrity
      if (!this.validateKeyIntegrity(keyMetadata)) {
        throw new Error('Invalid backup data');
      }

      // Store with new passcode
      await this.storeKeysSecurely(keyMetadata, newPasscode);

      return {
        success: true,
        message: 'Keys imported successfully',
        keyVersion: keyMetadata.keyVersion
      };
    } catch (error) {
      console.error('Key backup import failed:', error);
      throw new Error('Failed to import keys from backup');
    }
  }
}

// Create singleton instance
const keyManagementService = new KeyManagementService();

export default keyManagementService;
